import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command';

export interface MultiSelectOption {
    value: string;
    label: string;
}

interface MultiSelectProps {
    id?: string;
    options: MultiSelectOption[];
    selected: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    className?: string;
}

/** Searchable multi-select built on Popover + cmdk Command — used throughout
 * the Advanced Filters drawer for Category/Brand/Vendor/Status/etc. */
export const MultiSelect: React.FC<MultiSelectProps> = ({
    id,
    options,
    selected,
    onChange,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results found.',
    className,
}) => {
    const [open, setOpen] = useState(false);

    const toggle = (value: string) => {
        onChange(
            selected.includes(value)
                ? selected.filter((v) => v !== value)
                : [...selected, value]
        );
    };

    const selectedLabels = options.filter((o) => selected.includes(o.value));

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    id={id}
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "h-auto min-h-11 sm:min-h-9 w-full cursor-pointer justify-between px-3 py-1.5 font-normal",
                        className
                    )}
                >
                    {selectedLabels.length === 0 ? (
                        <span className="text-muted-foreground">{placeholder}</span>
                    ) : (
                        <span className="flex flex-1 flex-wrap gap-1 py-0.5">
                            {selectedLabels.map((o) => (
                                <Badge
                                    key={o.value}
                                    variant="secondary"
                                    className="gap-1 pr-1"
                                >
                                    {o.label}
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        className="cursor-pointer rounded-full hover:bg-muted-foreground/20"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggle(o.value);
                                        }}
                                    >
                                        <X className="h-3 w-3" />
                                    </span>
                                </Badge>
                            ))}
                        </span>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = selected.includes(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() => toggle(option.value)}
                                    >
                                        <span
                                            className={cn(
                                                "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                                                isSelected ? "bg-primary text-primary-foreground" : "opacity-50"
                                            )}
                                        >
                                            {isSelected && <Check className="h-3 w-3" />}
                                        </span>
                                        {option.label}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
